import {
  PageMappingType,
  RuleOperator,
  type Page,
  type PageGroupDetail,
  type PageGroupListItem,
  type PageGroupWritePayload,
  type PageMapping,
  type PageMappingApiPayload,
} from '@/modules/Pages/declarations/pages';
import { preparePageMappingForPayload, resolveOperatorForMapping } from '@/modules/Pages/utils/pageRules';

function normalizeMappingOperator(mapping: PageMapping): RuleOperator | undefined {
  return resolveOperatorForMapping(mapping);
}

function toApiJavascript(mapping: PageMapping, primaryValue: string): string | null {
  if (mapping.type === PageMappingType.JAVASCRIPT) {
    return mapping.javascript ?? primaryValue ?? null;
  }
  if (mapping.type === PageMappingType.REGEX) {
    return mapping.javascript ?? null;
  }
  return null;
}

export function normalizePageMappingFromApi(mapping: PageMapping): PageMapping {
  const values = [...(mapping.values ?? [])];

  if (mapping.type === PageMappingType.JAVASCRIPT && mapping.javascript && values.length === 0) {
    values.push(mapping.javascript);
  }

  if (mapping.type === PageMappingType.REGEX && mapping.regex && values.length === 0) {
    values.push(mapping.regex);
  }

  return {
    ...mapping,
    values,
    regex: mapping.regex ?? null,
    javascript: mapping.javascript || null,
    operator: normalizeMappingOperator(mapping),
  };
}

function toApiOperator(mapping: PageMapping): RuleOperator | null | undefined {
  if (mapping.type === PageMappingType.URL_CONTAINS || mapping.type === PageMappingType.REGEX) {
    return mapping.operator ?? null;
  }
  return undefined;
}

export function toApiPageMapping(mapping: PageMapping): PageMappingApiPayload {
  const prepared = preparePageMappingForPayload(mapping);
  const primaryValue = prepared.values[0] ?? '';

  return {
    id: prepared.id,
    type: prepared.type,
    values: prepared.values,
    propertyIds: prepared.propertyIds,
    priority: prepared.priority,
    javascript: toApiJavascript(prepared, primaryValue),
    regex: prepared.type === PageMappingType.REGEX
      ? prepared.regex ?? primaryValue ?? null
      : null,
    operator: toApiOperator(prepared),
  };
}

export function toApiPageGroupPayload(page: Page): PageGroupWritePayload {
  return {
    id: page.id,
    name: page.name,
    pages: (page.pages ?? []).map(toApiPageMapping),
  };
}

export function normalizePageGroupListItem(
  item: PageGroupListItem,
  index: number,
): Page {
  return {
    id: item.id,
    name: item.name,
    propertyIds: item.propertyIds ?? [],
    priority: index,
  };
}

export function normalizePageGroupDetail(data: PageGroupDetail): Page {
  return {
    id: data.id,
    name: data.name,
    pages: (data.pages ?? []).map(normalizePageMappingFromApi),
  };
}

export function isDuplicatePageGroupName(
  pages: Pick<Page, 'id' | 'name'>[],
  name: string,
  excludePageId?: string,
): boolean {
  const normalizedName = name.trim();
  if (!normalizedName) {
    return false;
  }

  return pages.some(
    (page) => page.id !== excludePageId && page.name?.trim() === normalizedName,
  );
}

const PAGE_NAME_SUFFIX_PATTERN = /^(.+) \((\d+)\)$/;

export function extractPageNameRoot(name: string): string {
  const trimmed = name.trim();
  const match = PAGE_NAME_SUFFIX_PATTERN.exec(trimmed);
  return match?.[1] ?? trimmed;
}

export function getNextAvailablePageName(
  name: string,
  pages: Pick<Page, 'id' | 'name'>[],
  excludePageId?: string,
): string {
  const root = extractPageNameRoot(name);
  const taken = new Set(
    pages
      .filter((page) => page.id !== excludePageId && page.name?.trim())
      .map((page) => page.name!.trim()),
  );

  if (!taken.has(root)) {
    return root;
  }

  let suffix = 1;

  while (taken.has(`${root} (${suffix})`)) {
    suffix += 1;
  }

  return `${root} (${suffix})`;
}
